import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";

const IN_PROGRESS_STATUS = ["uploaded", "security_scanning", "processing_preview", "extracting_text"];

const PROC_LABEL = {
  uploaded: "Queued for processing…",
  security_scanning: "Security scanning…",
  processing_preview: "Generating preview…",
  extracting_text: "Extracting text…",
};

// Viewer-first: renders a canonical document IN-APP. PDFs/images/text render
// natively; office files render via their generated PDF rendition; office files
// without a preview surface their canonical state (processing/failed/missing)
// with Retry and Download actions driven by preview.retryable — not overall status.
export default function UniversalDocumentViewer({ docId, preview, processingStatus, displayName, onRetry, className = "" }) {
  const token = getToken() || "";
  const variant = (preview?.kind === "pdf" && preview?.native === false)
    ? "preview"
    : (preview?.kind === "image" && preview?.viewer_preview_available)
      ? "image_preview"
      : "original";
  const contentUrl = `${API}/documents-intel/${docId}/content?disposition=inline&variant=${variant}&auth=${encodeURIComponent(token)}`;
  const downloadUrl = `${API}/documents-intel/${docId}/content?disposition=attachment&auth=${encodeURIComponent(token)}`;

  // Canonical preview state from the backend projection.
  // Falls back to legacy logic for documents processed before this field was added.
  const previewState = preview?.state || (preview?.available ? "ready" : null);

  const [text, setText] = useState(null);
  const [textErr, setTextErr] = useState(false);
  const [zoom, setZoom] = useState(1);

  // PDF content fetch state — switches from iframe-src to blob URL so HTTP
  // errors (storage missing, temporary failure) can be caught and surfaced
  // as user-facing messages instead of a silent browser error page.
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFetchState, setPdfFetchState] = useState(null); // null | "loading" | "missing" | "unavailable"
  const [pdfRetryKey, setPdfRetryKey] = useState(0);

  const retryPdfFetch = useCallback(() => {
    setPdfFetchState(null);
    setPdfRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (previewState !== "ready" || preview?.kind !== "text") return;
    setText(null); setTextErr(false);
    api.get(`/documents-intel/${docId}/text`)
      .then((r) => setText(r.data.text || ""))
      .catch(() => setTextErr(true));
  }, [docId, previewState, preview?.kind]);

  // PDF content fetch — blob-based so storage errors become visible
  useEffect(() => {
    if (previewState !== "ready" || preview?.kind !== "pdf") {
      return;
    }
    let active = true;
    let blobUrl = null;
    setPdfBlobUrl(null);
    setPdfFetchState("loading");
    fetch(contentUrl)
      .then((r) => {
        if (!active) return null;
        if (r.status === 409) { setPdfFetchState("missing"); return null; }
        if (!r.ok) { setPdfFetchState("unavailable"); return null; }
        return r.blob();
      })
      .then((blob) => {
        if (!active || !blob) return;
        blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
        setPdfFetchState(null);
      })
      .catch(() => { if (active) setPdfFetchState("unavailable"); });
    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [contentUrl, previewState, preview?.kind, pdfRetryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const Gate = ({ icon, tone, title, sub, testId, children }) => {
    const Ic = Icons[icon];
    return (
      <div
        className={`h-full flex flex-col items-center justify-center text-center p-8 ${tone}`}
        {...(testId ? { "data-testid": testId } : {})}
      >
        <Ic className={`h-12 w-12 mb-3 ${icon === "Loader2" ? "animate-spin" : ""}`} />
        <div className="text-sm font-medium">{title}</div>
        {sub && <div className="text-xs opacity-70 mt-1 max-w-xs">{sub}</div>}
        {children}
      </div>
    );
  };

  const DownloadBtn = () => (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-sm border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50"
      data-testid="viewer-download-original"
    >
      <Icons.Download className="h-4 w-4" />Download original
    </a>
  );

  const RetryBtn = ({ label = "Retry Processing" }) => (
    onRetry ? (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-2"
        data-testid="viewer-retry"
      >
        <Icons.RefreshCw className="h-4 w-4" />{label}
      </button>
    ) : null
  );

  let body;

  // ── Active pipeline job (overall processing OR preview being generated) ────
  if (previewState === "processing" || IN_PROGRESS_STATUS.includes(processingStatus)) {
    const label = PROC_LABEL[processingStatus] || preview?.message || "Processing…";
    body = (
      <Gate icon="Loader2" tone="bg-slate-50 text-slate-500" title={label}
            sub="This file is being prepared. You can keep working elsewhere." />
    );

  // ── Security hold ─────────────────────────────────────────────────────────
  } else if (
    processingStatus === "quarantined" ||
    processingStatus === "pending_security_review" ||
    previewState === "security_hold"
  ) {
    body = (
      <Gate icon="ShieldAlert" tone="bg-rose-50 text-rose-700"
            title="Held for security review"
            sub="This file cannot be opened while security review is pending." />
    );

  // ── Full processing failure (pipeline error — separate from preview failure) ──
  } else if (processingStatus === "processing_failed") {
    body = (
      <Gate icon="AlertTriangle" tone="bg-amber-50 text-amber-700"
            title="Processing failed"
            sub="The document uploaded but processing could not be completed.">
        <div className="mt-4 flex gap-2 flex-wrap justify-center">
          <RetryBtn />
          <DownloadBtn />
        </div>
      </Gate>
    );

  // ── Preview ready ─────────────────────────────────────────────────────────
  } else if (previewState === "ready" && preview?.kind === "pdf") {
    if (pdfFetchState === "loading" || (pdfFetchState === null && !pdfBlobUrl)) {
      body = (
        <Gate icon="Loader2" tone="bg-slate-50 text-slate-500"
              title="Loading document…" sub="Fetching document content." />
      );
    } else if (pdfFetchState === "missing") {
      body = (
        <Gate icon="FileX" tone="bg-amber-50 text-amber-700"
              title="Document content unavailable"
              sub="This file could not be located. Please contact your advisor for assistance."
              testId="viewer-pdf-missing" />
      );
    } else if (pdfFetchState === "unavailable") {
      body = (
        <Gate icon="CloudOff" tone="bg-slate-50 text-slate-500"
              title="Temporarily unavailable"
              sub="This document could not be loaded right now. Please try again.">
          <div className="mt-4">
            <button
              onClick={retryPdfFetch}
              className="inline-flex items-center gap-1.5 text-sm border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50"
              data-testid="viewer-pdf-retry"
            >
              <Icons.RefreshCw className="h-4 w-4" />Try again
            </button>
          </div>
        </Gate>
      );
    } else if (pdfBlobUrl) {
      body = (
        <iframe
          title={displayName || "document"}
          src={`${pdfBlobUrl}#toolbar=1&navpanes=0`}
          className="w-full h-full"
          data-testid="viewer-pdf"
        />
      );
    } else {
      body = (
        <Gate icon="Loader2" tone="bg-slate-50 text-slate-500" title="Loading document…" />
      );
    }
  } else if (previewState === "ready" && preview?.kind === "image") {
    body = (
      <div className="w-full h-full overflow-auto flex items-center justify-center">
        <div className="absolute top-2 right-2 z-10 flex gap-1 bg-white/90 rounded-lg shadow px-1 py-0.5">
          <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} className="p-1.5 hover:bg-slate-100 rounded" title="Zoom out"><Icons.ZoomOut className="h-4 w-4" /></button>
          <button onClick={() => setZoom(1)} className="p-1.5 hover:bg-slate-100 rounded text-xs">{Math.round(zoom * 100)}%</button>
          <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="p-1.5 hover:bg-slate-100 rounded" title="Zoom in"><Icons.ZoomIn className="h-4 w-4" /></button>
        </div>
        <img
          src={contentUrl}
          alt={displayName}
          style={{ transform: `scale(${zoom})` }}
          className="max-w-full max-h-full object-contain transition-transform"
          data-testid="viewer-image"
        />
      </div>
    );
  } else if (previewState === "ready" && preview?.kind === "text") {
    body = (
      <div className="w-full h-full overflow-auto bg-white p-4" data-testid="viewer-text">
        {text === null && !textErr && <div className="flex items-center gap-2 text-slate-400 text-sm"><Icons.Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {textErr && <div className="text-sm text-slate-500">Could not load text content.</div>}
        {text !== null && <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">{text || "(empty file)"}</pre>}
      </div>
    );

  // ── Preview generation previously failed ──────────────────────────────────
  } else if (previewState === "failed") {
    body = (
      <Gate icon="AlertTriangle" tone="bg-amber-50 text-amber-700"
            title="Preview generation failed"
            sub={preview?.message || "The document uploaded but the preview could not be generated."}>
        <div className="mt-4 flex gap-2 flex-wrap justify-center">
          {preview?.retryable && <RetryBtn label="Retry Preview" />}
          <DownloadBtn />
        </div>
      </Gate>
    );

  // ── Preview never generated (legacy doc or first upload) ──────────────────
  } else if (previewState === "missing") {
    body = (
      <Gate icon="FileQuestion" tone="bg-slate-50 text-slate-500"
            title="Preview not yet generated"
            sub={preview?.message || "Preview has not been generated for this document."}>
        <div className="mt-4 flex gap-2 flex-wrap justify-center">
          {preview?.retryable && <RetryBtn label="Generate Preview" />}
          <DownloadBtn />
        </div>
      </Gate>
    );

  // ── Unsupported format or unknown state ───────────────────────────────────
  } else {
    body = (
      <Gate icon="FileQuestion" tone="bg-slate-50 text-slate-500"
            title={preview?.message || "Preview unavailable for this file format"}
            sub="The original file is securely stored and can be downloaded.">
        <div className="mt-4">
          <DownloadBtn />
        </div>
      </Gate>
    );
  }

  return (
    <div
      className={`relative w-full h-full bg-slate-100 rounded-lg overflow-hidden border border-slate-200 ${className}`}
      data-testid="universal-document-viewer"
    >
      {body}
    </div>
  );
}
